import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchTicket } from 'flow/actions/tickets-action';
import { selectTicketIsFetched } from 'flow/selectors/TicketsSelectors';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { TicketKey } from 'flow/types/EntityKeys';
import { ticketFetchErrorMeansNoAccess } from 'flow/utils/ticket-utils';

/**
 * Fetches the ticket every time a ticket view mounts, and again on every ticket
 * key change, even when the store already holds the ticket.
 *
 * The backend resolves `viewerPermissions` per viewer, and a change to it is not
 * pushed. Opening the ticket is therefore the only moment the client can pick up
 * an access change a colleague made. A stored ticket stays on screen while the
 * request runs, so a refetch shows no loading state.
 *
 * A refetch the backend refuses means the viewer has no access. The caller then
 * renders the missing-access state instead of controls the backend now refuses.
 *
 * It also asks again whenever the ticket leaves the store under a mounted view,
 * which happens when a colleague revokes access to a ticket the viewer is not
 * looking at from the tickets route — nothing navigates away from those, so the
 * refused answer is the only thing that tells the view to stop rendering it.
 */
const useTicketFetchOnTicketViewMount = (ticketKey: TicketKey) => {
    const dispatch = useAppDispatch();
    const [accessToTicketMissing, setAccessToTicketMissing] = useState(false);
    const [requestIsInFlight, setRequestIsInFlight] = useState(false);

    const ticketIsFetched = useAppSelector((state) =>
        selectTicketIsFetched(state, ticketKey),
    );

    const askWhetherTicketIsStillReadable = useCallback(() => {
        let viewIsStillMounted = true;

        setAccessToTicketMissing(false);
        setRequestIsInFlight(true);

        dispatch(fetchTicket(ticketKey))
            .catch((response: Response) => {
                if (
                    viewIsStillMounted &&
                    ticketFetchErrorMeansNoAccess(response)
                ) {
                    setAccessToTicketMissing(true);
                }
            })
            .finally(() => {
                if (viewIsStillMounted) {
                    setRequestIsInFlight(false);
                }
            });

        return () => {
            viewIsStillMounted = false;
        };
    }, [dispatch, ticketKey]);

    useEffect(askWhetherTicketIsStillReadable, [
        askWhetherTicketIsStillReadable,
    ]);

    // Only the ticket leaving the store is worth asking about again. Depending on
    // the flag itself would ask a second time on any open that starts without the
    // ticket, since the first answer puts it there.
    const ticketInStoreBefore = useRef({ ticketKey, ticketIsFetched });
    useEffect(() => {
        const before = ticketInStoreBefore.current;
        const ticketLeftTheStore =
            before.ticketKey === ticketKey &&
            before.ticketIsFetched &&
            !ticketIsFetched;
        ticketInStoreBefore.current = { ticketKey, ticketIsFetched };

        if (!ticketLeftTheStore) return undefined;
        return askWhetherTicketIsStillReadable();
    }, [ticketKey, ticketIsFetched, askWhetherTicketIsStillReadable]);

    return {
        accessToTicketMissing,
        ticketIsFetched,
        // Only a first fetch has nothing to show, so only a first fetch loads.
        isFetchingTicket: requestIsInFlight && !ticketIsFetched,
    };
};

export default useTicketFetchOnTicketViewMount;
